import { useState, useEffect } from 'react';
import { Bars3Icon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/helpers';
import { useAppDispatch } from '@/hooks';
import { toggleMobileLeftDrawer, toggleMobileRightDrawer } from '@/store/slices/uiSlice';
import { useCompactHeaderContent, useForceCompact, useHeaderHidden } from './MobilePanelContext';

export function Header() {
  const dispatch = useAppDispatch();
  const compactHeaderContent = useCompactHeaderContent();
  const forceCompact = useForceCompact();
  const headerHidden = useHeaderHidden();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const showCompact = (forceCompact || scrolled) && !!compactHeaderContent;

  if (headerHidden) return null;

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-30 h-12 transition-all duration-300',
        scrolled
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm shadow-black/[0.03] dark:shadow-black/20'
          : ''
      )}
    >
      <div className="flex h-full items-center justify-between px-1">
        {/* Left: Hamburger */}
        <button
          onClick={() => dispatch(toggleMobileLeftDrawer())}
          className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors shrink-0"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>

        {/* Center: Logo or Compact Content */}
        <div className="relative flex-1 flex items-center justify-center min-w-0 mx-1">
          {/* Logo - fades out when compact shown */}
          <div
            className={cn(
              'flex items-center gap-2 transition-all duration-300',
              showCompact ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'
            )}
          >
            <svg width="24" height="24" viewBox="30 50 300 330" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="hdrTop" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FFEEE4"/>
                  <stop offset="100%" stopColor="#FFE4D6"/>
                </linearGradient>
                <linearGradient id="hdrLeft" x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FDBA74"/>
                  <stop offset="100%" stopColor="#FB923C"/>
                </linearGradient>
                <linearGradient id="hdrRight" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F97316"/>
                  <stop offset="100%" stopColor="#EA580C"/>
                </linearGradient>
              </defs>
              <g>
                <path d="M40 250 L40 340 L100 370 L100 280 Z" fill="url(#hdrLeft)"/>
                <path d="M100 280 L100 370 L160 340 L160 250 Z" fill="url(#hdrRight)"/>
                <path d="M40 250 L100 220 L160 250 L100 280 Z" fill="url(#hdrTop)"/>
              </g>
              <g>
                <path d="M120 180 L120 340 L180 370 L180 210 Z" fill="url(#hdrLeft)"/>
                <path d="M180 210 L180 370 L240 340 L240 180 Z" fill="url(#hdrRight)"/>
                <path d="M120 180 L180 150 L240 180 L180 210 Z" fill="url(#hdrTop)"/>
              </g>
              <g>
                <path d="M200 90 L200 340 L260 370 L260 120 Z" fill="url(#hdrLeft)"/>
                <path d="M260 120 L260 370 L320 340 L320 90 Z" fill="url(#hdrRight)"/>
                <path d="M200 90 L260 60 L320 90 L260 120 Z" fill="url(#hdrTop)"/>
              </g>
            </svg>
            <span className="text-base font-semibold text-gray-900 dark:text-white">
              DayDrive
            </span>
          </div>

          {/* Compact content - fades in when scrolled */}
          {compactHeaderContent && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center min-w-0 transition-all duration-300',
                showCompact
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-2 pointer-events-none'
              )}
            >
              {compactHeaderContent}
            </div>
          )}
        </div>

        {/* Right: 3-dots */}
        <button
          onClick={() => dispatch(toggleMobileRightDrawer())}
          className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors shrink-0"
        >
          <EllipsisVerticalIcon className="w-6 h-6" />
        </button>
      </div>
    </header>
  );
}
